import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { I18nProvider, LangSwitch, useI18n } from "./lib/i18n";
import { Login } from "./pages/Login";
import { Dishes } from "./pages/Dishes";
import { NewDish } from "./pages/NewDish";
import { DishDetail } from "./pages/DishDetail";
import { DishMaterials } from "./pages/DishMaterials";
import { SopViewer } from "./pages/SopViewer";
import { Review } from "./pages/Review";
import { ManualEdit } from "./pages/ManualEdit";
import { Evaluations, EvaluationReport } from "./pages/Evaluations";
import { Proficiency } from "./pages/Proficiency";
import { Ops } from "./pages/Ops";
import { Users } from "./pages/Users";
import { Presence } from "./pages/Presence";
import { PresenceGate } from "./components/PresenceGate";
import { ROLE_LABEL } from "./lib/types";

function Shell() {
  const { user, profile, loading, needsProvisioning, logout } = useAuth();
  const { t } = useI18n();
  const isStaff = profile?.role === "admin" || profile?.role === "chef";

  if (loading) return <div className="center"><p className="muted">{t("読み込み中…")}</p></div>;
  if (!user) return <Login />;

  // 認証は通ったが users/{uid} が無い。権限の自己付与を防ぐため、
  // ここから先には進ませない(管理者が登録するまで待つ)。
  if (needsProvisioning) {
    return (
      <div className="center">
        <div className="card narrow">
          <h1>{t("登録待ち")}</h1>
          <p>
            {t("{email} でログインしましたが、このアカウントはまだ利用者として登録されていません。", {
              email: user.email ?? "",
            })}
          </p>
          <p className="muted">
            {t("管理者が役割と担当品目を設定すると利用できるようになります。")}
          </p>
          <button onClick={logout}>{t("ログアウト")}</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <nav className="nav">
        <span className="brand">
          <span className="stamp" aria-hidden="true">一人<br />前</span>
          {t("イチニンマエ")}
        </span>
        <Link to="/">{t("品目")}</Link>
        <Link to="/evaluations">{t("作業の評価")}</Link>
        {isStaff && <Link to="/proficiency">{t("習熟度")}</Link>}
        {profile?.role === "admin" && <Link to="/presence">{t("同席コード")}</Link>}
        {profile?.role === "admin" && <Link to="/users">{t("利用者")}</Link>}
        <span className="spacer" />
        <LangSwitch />
        <span className="muted small">
          {profile?.displayName || user.email}
          {profile && ` / ${t(ROLE_LABEL[profile.role])}`}
        </span>
        <button className="link" onClick={logout}>{t("ログアウト")}</button>
      </nav>
      <Routes>
        <Route path="/" element={<Dishes />} />
        <Route path="/dishes/new" element={<NewDish />} />
        <Route path="/dishes/:dishId" element={<DishDetail />} />
        <Route path="/dishes/:dishId/materials" element={<DishMaterials />} />
        {/* 作業者は管理者の同席コードを入力してから開く（本文を出す画面だけ） */}
        <Route path="/dishes/:dishId/view" element={<PresenceGate><SopViewer /></PresenceGate>} />
        <Route path="/dishes/:dishId/review" element={<PresenceGate><Review /></PresenceGate>} />
        <Route path="/dishes/:dishId/edit" element={<ManualEdit />} />
        <Route path="/evaluations" element={<Evaluations />} />
        <Route path="/proficiency" element={<Proficiency />} />
        <Route path="/users" element={<Users />} />
        <Route path="/presence" element={<Presence />} />
        {/* S7 運用コンソール。ナビには出さない（顧客に存在を見せない）。SAKAMA内部向けのため多言語化の対象外 */}
        <Route path="/ops" element={<Ops />} />
        <Route path="/evaluations/:evalId" element={<EvaluationReport />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
